import { toast } from 'react-toastify';
import { onSubmitKeyhandOffProcess } from 'src/services/client';
import { useState } from 'react';

export interface KeyHandoffParams {
  SuiteId: string;
  LoginUserId: string;
  SignatureList: any[];
  notes: string;
  warrantyStartDate: string;
}

export interface UseKeyHandoffSubmissionReturn {
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  refetchPageDataFromAPI: boolean;
  submitKeyHandoff: (params: KeyHandoffParams) => Promise<boolean>;
  resetState: () => void;
}

/* Custom hook for handling key handoff submission process Returns a boolean indicating success/failure
 * so components can handle post-submission logic (like closing dialogs) appropriately. */
export const useKeyHandoffSubmission = (): UseKeyHandoffSubmissionReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchPageDataFromAPI, setRefetchPageDataFromAPI] =
    useState<boolean>(false);

  const submitKeyHandoff = async (
    params: KeyHandoffParams
  ): Promise<boolean> => {
    try {
      setError(null);
      setIsSubmitting(true);

      // Make API call to complete the Key Handoff Process
      const response: any = await onSubmitKeyhandOffProcess(params);

      if (response.status !== 200) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const responseJson: any = await response.json();

      if (responseJson.statusCode !== 200) {
        throw new Error('Key Pickup process submit failed');
      }

      setIsSuccess(true);
      setRefetchPageDataFromAPI(true);

      toast.success('ⓘ Key Pickup process submitted successfully.', {
        autoClose: 10000,
        position: 'top-center',
      });

      return true;
    } catch (error: any) {
      console.error('Key Pickup submission error:', error);

      const errorMessage: any =
        error.message ||
        'Something went wrong with submitting the Key Pickup process';
      setError(errorMessage);

      toast.error(`🚫 ${errorMessage}, please try again.`, {
        autoClose: 12000,
        position: 'top-center',
      });

      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetState = () => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError(null);
    setRefetchPageDataFromAPI(false); // This was missing!
  };

  return {
    isSubmitting,
    isSuccess,
    error,
    refetchPageDataFromAPI,
    submitKeyHandoff,
    resetState,
  };
};
